import { useState, type UIEvent } from "react";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useUnreadCount,
  useInfiniteInAppNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
} from "@/hooks/api/useNotifications";
import type { InAppNotification } from "@/api/types/notifications";
import { NotificationDetailModal } from "./NotificationDetailModal";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<InAppNotification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch unread count on load
  const {
    data: unreadData,
    loading: unreadLoading,
    refetch: refetchUnread,
  } = useUnreadCount();

  // Fetch notifications
  const {
    data: notificationsData,
    loading: notificationsLoading,
    refetch: refetchNotifications,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteInAppNotifications({ limit: 10 });

  // Mutations
  const { execute: markAsRead, loading: markingRead } = useMarkAsRead();
  const { execute: markAllAsRead, loading: markingAllRead } =
    useMarkAllAsRead();

  const unreadCount = unreadData?.unread_count ?? 0;
  // API returns array directly
  const notifications = notificationsData ?? [];

  const handleNotificationClick = async (notification: InAppNotification) => {
    setSelectedNotification(notification);
    setModalOpen(true);
    setOpen(false);

    // Mark as read if not already read
    if (!notification.read_at) {
      await markAsRead(notification.notification_date);
      refetchUnread();
      refetchNotifications();
    }
  };

  const handleMarkAllAsRead = async () => {
    const result = await markAllAsRead();
    if (result !== null) {
      refetchUnread();
      refetchNotifications();
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedNotification(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleNotificationsScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage || notificationsLoading) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 48;

    if (nearBottom) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative rounded-full bg-white/10 hover:bg-white/20"
          >
            <Bell className="h-5 w-5 text-white" />
            {/* Badge for unread count */}
            {!unreadLoading && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto py-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
              >
                {markingAllRead ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <CheckCheck className="h-3 w-3 mr-1" />
                )}
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          <div className="h-[300px] overflow-y-auto" onScroll={handleNotificationsScroll}>
            {notificationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="h-10 w-10 text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No notifications yet
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.notification_date}
                    notification={notification}
                    onClick={() => handleNotificationClick(notification)}
                    formatDate={formatDate}
                    disabled={markingRead}
                  />
                ))}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {!isFetchingNextPage && notifications.length > 0 && !hasNextPage && (
                  <div className="py-3 text-center text-xs text-muted-foreground">
                    No more notifications
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Detail Modal */}
      <NotificationDetailModal
        open={modalOpen}
        onClose={handleModalClose}
        notification={selectedNotification}
      />
    </>
  );
}

interface NotificationItemProps {
  notification: InAppNotification;
  onClick: () => void;
  formatDate: (date: string) => string;
  disabled?: boolean;
}

function NotificationItem({
  notification,
  onClick,
  formatDate,
  disabled,
}: NotificationItemProps) {
  const isUnread = !notification.read_at;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full text-left px-4 py-3 transition-colors",
        "focus:outline-none",
        // Unread: subtle neutral tint background, Read: clean white/transparent
        isUnread
          ? "bg-gray-100/60 hover:bg-gray-100 dark:bg-gray-800/40 dark:hover:bg-gray-800/60"
          : "bg-transparent hover:bg-muted/30",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Unread indicator */}
        <div className="pt-1.5">
          <span
            className={cn(
              "block h-2 w-2 rounded-full",
              isUnread ? "bg-gray-500" : "bg-transparent"
            )}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Main message: "{alert_count} alerts triggered on {notification_date}" */}
          <p
            className={cn(
              "text-sm",
              isUnread
                ? "font-semibold text-foreground"
                : "text-muted-foreground"
            )}
          >
            {notification.alert_count} alert
            {notification.alert_count !== 1 ? "s" : ""} triggered on{" "}
            {formatDate(notification.notification_date)}
          </p>

          {/* Forecast dates range if available */}
          {notification.forecast_dates &&
            notification.forecast_dates.length > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Forecasts:{" "}
                {notification.forecast_dates.length > 1
                  ? `${formatDate(
                      notification.forecast_dates[0]
                    )} - ${formatDate(
                      notification.forecast_dates[
                        notification.forecast_dates.length - 1
                      ]
                    )}`
                  : formatDate(notification.forecast_dates[0])}
              </p>
            )}

          {/* Created time */}
          <p className="text-xs text-muted-foreground/70 mt-1">
            {formatDate(notification.created_at)}
          </p>
        </div>
      </div>
    </button>
  );
}

export default NotificationBell;
